import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const FISCAL_FIELDS = [
  ["tax_id", "NIF / CIF / NIE", "12345678Z o B12345678", "sm:col-span-1"],
  ["fiscal_address", "Dirección fiscal", "C/ Mayor 1, 2º B", "sm:col-span-2"],
  ["postal_code", "Código postal", "28001", "sm:col-span-1"],
  ["city", "Ciudad", "Madrid", "sm:col-span-1"],
  ["province", "Provincia", "Madrid", "sm:col-span-1"],
];

export const isFiscalComplete = (u) => FISCAL_FIELDS.every(([k]) => (u?.[k] || "").trim());

export function FiscalFields({ form, setForm }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {FISCAL_FIELDS.map(([k, label, ph, span]) => (
        <div key={k} className={span}>
          <label className="kbar block">{label}</label>
          <input data-testid={`fiscal-${k}`} value={form[k]} placeholder={ph}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className={`w-full h-12 mt-2 px-3 border border-slate-200 outline-none focus:border-orange-600 ${k === "tax_id" || k === "postal_code" ? "font-mono uppercase" : ""}`} />
        </div>
      ))}
    </div>
  );
}

export function FiscalDataSection() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#fiscal") document.getElementById("fiscal")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(() => Object.fromEntries(FISCAL_FIELDS.map(([k]) => [k, user?.[k] || ""])));
  const [saving, setSaving] = useState(false);
  const complete = isFiscalComplete(user);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setForm(Object.fromEntries(FISCAL_FIELDS.map(([k]) => [k, data?.[k] || ""])));
      await refresh();
      toast.success("Datos fiscales guardados");
    } catch (err) { toast.error(err?.response?.data?.detail || "Error guardando"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} id="fiscal" className="mt-px border border-slate-200 bg-white p-6 scroll-mt-6" data-testid="fiscal-data-section">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <Landmark className="w-5 h-5 text-slate-400" />
        <h2 className="font-display font-bold text-xl">Datos fiscales del emisor</h2>
        {complete ? (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1" data-testid="fiscal-status-complete">
            <CheckCircle2 className="w-3.5 h-3.5" /> Completos
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1" data-testid="fiscal-status-incomplete">
            <AlertTriangle className="w-3.5 h-3.5" /> Obligatorios para emitir facturas
          </span>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-5">Aparecen en la cabecera de tus facturas y presupuestos. Son obligatorios para emitir facturas (RD 1619/2012).</p>
      <FiscalFields form={form} setForm={setForm} />
      <button disabled={saving} data-testid="fiscal-save" className="btn-brand h-11 px-5 mt-5">{saving ? "Guardando…" : "Guardar datos fiscales"}</button>
    </form>
  );
}
